package com.dotdrives.web.pages.forms

import com.dotdrives.model.GivingFormStatus
import com.dotdrives.controller.FormBuilderUiState
import com.dotdrives.web.pages.drives.formatMoneyFull
import com.dotdrives.web.pages.giving.givingContactFields
import kotlin.time.Clock
import react.FC
import react.Key
import react.Props
import react.useState
import react.dom.html.ReactHTML.button
import web.html.ButtonType
import web.html.button as buttonType
import react.dom.html.ReactHTML.a
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.span
import web.cssom.ClassName
import web.window.WindowTarget

external interface FormBuilderPreviewProps : Props {
    var state: FormBuilderUiState
    var orgId: String
}

/**
 * A compact draft preview. The saved public link resolves its own allowlisted Edge projection;
 * private staff data and unsaved configuration are never sent to the anonymous form.
 */
val FormBuilderPreview = FC<FormBuilderPreviewProps> { props ->
    val state = props.state
    val form = state.form ?: return@FC

    div {
        className = ClassName("forms-builder-preview")
        div {
            className = ClassName("forms-preview-card")
            div {
                className = ClassName("forms-preview-head")
                div {
                    className = ClassName("forms-preview-headline")
                    +state.draftHeadline.ifBlank { "Your headline goes here" }
                }
                div {
                    className = ClassName("forms-preview-target")
                    +(form.targetName ?: "General giving")
                }
            }
            div {
                className = ClassName("forms-preview-body")
                if (state.draftAmounts.isEmpty()) {
                    div {
                        className = ClassName("forms-preview-empty")
                        +"Add suggested amounts to preview them here"
                    }
                } else {
                    div {
                        className = ClassName("forms-preview-amounts")
                        state.draftAmounts.forEachIndexed { index, amount ->
                            div {
                                key = index.toString().unsafeCast<Key>()
                                className = ClassName(
                                    "forms-preview-amount" + if (amount.isDefault) " forms-preview-amount-selected" else "",
                                )
                                +formatMoneyFull(amount.amount)
                            }
                        }
                        if (state.draftToggles.customAmount) {
                            div { className = ClassName("forms-preview-amount"); +"Other" }
                        }
                    }
                }
                if (state.draftToggles.monthlyGiving || state.draftToggles.quarterlyGiving) {
                    div {
                        className = ClassName("forms-preview-freq")
                        div { className = ClassName("forms-preview-freq-opt forms-preview-freq-opt-active"); +"One time" }
                        if (state.draftToggles.monthlyGiving) div { className = ClassName("forms-preview-freq-opt"); +"Monthly 💛" }
                        if (state.draftToggles.quarterlyGiving) div { className = ClassName("forms-preview-freq-opt"); +"Quarterly" }
                    }
                }
                if (state.draftStory.isNotBlank()) {
                    div {
                        className = ClassName("forms-preview-story")
                        +(state.draftStory.take(240) + if (state.draftStory.length > 240) "…" else "")
                    }
                }
                val contactFields = givingContactFields(form.config.copy(fields = state.draftFields))
                contactFields.forEach { field ->
                    div {
                        key = "preview-${field.key}".unsafeCast<Key>()
                        className = ClassName("forms-preview-field")
                        val label = field.key.replace('_', ' ').replaceFirstChar { it.uppercase() }
                        +(label + if (field.required) " *" else "")
                    }
                }
                div {
                    className = ClassName("forms-preview-cta")
                    val defaultAmount = state.draftAmounts.firstOrNull { it.isDefault } ?: state.draftAmounts.firstOrNull()
                    +("Give " + (defaultAmount?.let { formatMoneyFull(it.amount) } ?: ""))
                }
                div {
                    className = ClassName("forms-preview-secure")
                    +"Payment details use hosted Stripe Checkout when online giving is enabled."
                }
                if (form.status == GivingFormStatus.LIVE) {
                    a {
                        className = ClassName("forms-preview-public-link")
                        href = publicFormUrl(props.orgId, form.publicSlug)
                        target = "_blank".unsafeCast<WindowTarget>()
                        rel = "noopener noreferrer"
                        +"Open saved public form ↗"
                    }
                } else {
                    div {
                        className = ClassName("forms-preview-link-note")
                        +if (form.status == GivingFormStatus.DRAFT)
                            "This draft has no active public link. Use Preview to test it without publishing."
                        else "This archived form has no active public link. You can still test it in Preview."
                    }
                }
            }
        }
    }
}

external interface FormSubmissionFeedPanelProps : Props {
    var state: FormBuilderUiState
}

val FormSubmissionFeedRail = FC<FormSubmissionFeedPanelProps> { props ->
    val (expanded, setExpanded) = useState(false)
    div {
        className = ClassName("forms-v2-feed-rail" + if (expanded) " forms-v2-feed-expanded" else "")
        button {
            type = ButtonType.buttonType; className = ClassName("forms-v2-feed-toggle")
            ariaExpanded = expanded; ariaControls = "form-recent-feed".unsafeCast<web.dom.ElementId>()
            ariaLabel = if (expanded) "Collapse recent feed" else "Expand recent feed"
            onClick = { setExpanded(!expanded) }
            span { +if (expanded) "›" else "‹" }; span { +"Recent feed" }
            span { className = ClassName("forms-v2-feed-count"); +(props.state.submissions?.size?.coerceAtMost(30)?.toString() ?: "—") }
        }
        if (expanded) div { id = "form-recent-feed".unsafeCast<web.dom.ElementId>(); FormSubmissionFeedPanel { state = props.state } }
    }
}

val FormSubmissionFeedPanel = FC<FormSubmissionFeedPanelProps> { props ->
    val submissions = props.state.submissions?.take(30)
    div {
        className = ClassName("forms-feed-panel")
        div { className = ClassName("forms-feed-head"); div { className = ClassName("forms-feed-title"); +"Recent submissions" } }
        div { className = ClassName("forms-v2-feed-note"); +"Last 30 days · Up to 30 most recent gifts" }
        if (submissions.isNullOrEmpty()) {
            div { className = ClassName("forms-feed-empty"); +"No submissions to show in this view for the last 30 days." }
        } else {
            div {
                className = ClassName("forms-feed-list")
                val now = Clock.System.now()
                submissions.forEach { submission ->
                    div {
                        key = submission.id.unsafeCast<Key>(); className = ClassName("forms-feed-item")
                        div {
                            className = ClassName("forms-feed-item-top")
                            span { className = ClassName("forms-feed-name"); +submission.donorName }
                            span { className = ClassName("forms-feed-amount"); +formatMoneyFull(submission.amount) }
                        }
                        div {
                            className = ClassName("forms-feed-item-bottom")
                            span { className = ClassName("forms-feed-meta"); +if (submission.isRecurring) "Monthly" else "One time" }
                            span { className = ClassName("forms-feed-when"); +relativeTimeLabel(submission.createdAt, now) }
                        }
                    }
                }
            }
            div {
                className = ClassName("forms-feed-footer")
                span { +"Shown gifts (${submissions.size})" }
                span { className = ClassName("forms-feed-footer-value"); +formatMoneyFull(submissions.sumOf { it.amount }) }
            }
            div { className = ClassName("forms-v2-feed-note"); +"This sum covers only the gifts shown, not the full period." }
        }
    }
}
